import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Award, ChevronRight, CheckCircle, XCircle, AlertCircle, BookOpen, TrendingUp, Eye, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getClassData } from './api';

const SubjectBadge = ({ subject }) => {
  const colors = {
    Mathematics: 'bg-purple-100 text-purple-800 border-purple-300',
    English: 'bg-blue-100 text-blue-800 border-blue-300',
    Physics: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    Chemistry: 'bg-green-100 text-green-800 border-green-300',
    History: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Computer Science': 'bg-red-100 text-red-800 border-red-300',
    Biology: 'bg-teal-100 text-teal-800 border-teal-300',
  };

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colors[subject] || 'bg-gray-100 text-gray-800 border-gray-300'}`}>
      {subject}
    </span>
  );
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'submitted':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'not submitted':
      return <XCircle className="w-5 h-5 text-red-500" />;
    case 'viewed':
      return <Eye className="w-5 h-5 text-blue-500" />;
    case 'not viewed':
      return <Clock className="w-5 h-5 text-yellow-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-yellow-500" />;
  }
};

export default function ExamInfo() {
  const [activeExams, setActiveExams] = useState([]);
  const [previousExams, setPreviousExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classData, setClassData] = useState(null);

  const navigate = useNavigate();
  const classRoomId = localStorage.getItem("classRoomId");
  const studentId = localStorage.getItem("studentId");

  useEffect(() => {
    const fetchExams = async () => {
      if (!classRoomId) {
        setError('Class information not found.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getClassData(classRoomId);
        setClassData(data);

        const currentDate = new Date();
        const active = [];
        const previous = [];

        data.exams.forEach(exam => {
          const examEndDate = new Date(exam.examEndDate);
          const studentSubmission = exam.examSubmissions.find(submission => submission.studentId === studentId);

          if (examEndDate > currentDate && !studentSubmission) {
            active.push(exam);
          } else {
            previous.push({
              ...exam,
              status: studentSubmission ? 'submitted' : 'not submitted',
              submissionId: studentSubmission ? studentSubmission.submissionId : null,
              obtainedMarks: studentSubmission ? studentSubmission.obtainedMarks : null
            });
          }
        });

        setActiveExams(active);
        setPreviousExams(previous);
        setError(null);
      } catch (err) {
        console.error('Error fetching exams:', err);
        setError('Failed to load exams. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, [classRoomId, studentId]);

  const handleStartExam = (examId) => {
    navigate(`/exam/details/${examId}/instruction`);
  };

  const handleViewDetails = (examId, submissionId) => {
    if (submissionId) {
      navigate(`/exam/submitted-exam/${submissionId}`);
    } else {
      alert("No submission found for this exam. Viewing exam details.");
      navigate(`/exam/details/${examId}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-100 to-purple-100">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center mt-8 text-red-600">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 bg-gradient-to-br from-blue-100 to-purple-100">
      <div className="flex flex-col md:flex-row justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4 md:mb-0">My Exams</h1>
        <div className="text-lg text-gray-600">
          Class: {classData?.classValue} | Subject: {classData?.subject}
        </div>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
          <BookOpen className="w-6 h-6 mr-2 text-blue-500" />
          Active Exams
        </h2>
        {activeExams.length === 0 ? (
          <p className="text-gray-600 text-center">No active exams at the moment.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeExams.map((exam) => (
              <div key={exam.examId} className="bg-white rounded-lg shadow-md overflow-hidden transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <SubjectBadge subject={classData?.subject} />
                    <span className="text-sm text-gray-500 flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {exam.examDuration} mins
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">{exam.exam_topic}</h3>
                  <div className="space-y-2 mb-6">
                    <p className="text-sm text-gray-600 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                      Start: {new Date(exam.examStartDate).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                      End: {new Date(exam.examEndDate).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Award className="w-4 h-4 mr-2 text-blue-500" />
                      Max Marks: {exam.maxMarks || 'N/A'}
                    </p>
                  </div>
                  <button 
                    onClick={() => handleStartExam(exam.examId)}
                    className="w-full bg-blue-500 text-white font-semibold py-2 px-4 rounded-lg transition duration-300 ease-in-out flex items-center justify-center hover:bg-blue-600"
                  >
                    Start Exam
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
          <TrendingUp className="w-6 h-6 mr-2 text-blue-500" />
          Previous Exams
        </h2>
        {previousExams.length === 0 ? (
          <p className="text-gray-600 text-center">No previous exams found.</p>
        ) : (
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {previousExams.map((exam) => (
                <li key={exam.examId} className="hover:bg-gray-50 transition duration-300 ease-in-out">
                  <div className="px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <StatusIcon status={exam.status} />
                      <div>
                        <h3 className="text-lg font-medium text-gray-800">{exam.exam_topic}</h3>
                        <div className="flex items-center mt-1">
                          <SubjectBadge subject={classData?.subject} />
                          <span className="ml-2 text-sm text-gray-500 capitalize">{exam.status}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {exam.status === 'submitted' ? (
                        <span className="text-sm font-medium text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                          Marks: {exam.obtainedMarks !== null ? exam.obtainedMarks : 'Pending'}/{exam.maxMarks || 'N/A'}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-red-600 bg-red-100 px-3 py-1 rounded-full">
                          Not Submitted
                        </span>
                      )}
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1 text-blue-500" />
                        Duration: {exam.examDuration} mins
                      </span>
                      <button 
                        onClick={() => handleViewDetails(exam.examId, exam.submissionId)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium transition duration-300 ease-in-out flex items-center"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

